import {useCallback, useRef} from 'react'
import {useCLIPromptState} from './use-cli-prompt-state'
import {useCreateCommand} from './useCreateCommand'
import {useOptimisticLogs} from '../contexts/OptimisticLogsContext'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'
import type {CommandType} from '../types/session'

export interface UsePromptResponseSubmissionOptions {
  /** The session whose prompt should be monitored and responded to. */
  sessionId: string | undefined
  /** Whether to enable the underlying CLI prompt state hook (typically: !!sessionId
   *  AND the session is in a state that can accept user input). */
  enabled: boolean
  /** Optional callback invoked when the prompt-response createCommand mutation
   *  fails, after the optimistic record has been rewound. Use this to surface
   *  a user-visible error in the consumer (e.g. setFailedToSubmit). */
  onError?: () => void
}

export interface PromptResponseSubmission {
  /** The current pending prompt, or null when none. */
  pendingPrompt: NormalizedPrompt | null
  /** True while the underlying logs query is loading. */
  isLoading: boolean
  /** True while a prompt-response createCommand mutation is in flight. */
  isPending: boolean
  /** Dismiss the current prompt without sending a response. */
  dismissPrompt: () => void
  /** Submit a response for the pending prompt. No-op when there is no pending
   *  prompt, a previous response is in flight, or sessionId is missing. */
  handlePromptRespond: (toolCallId: string, response: unknown, commandType: CommandType) => void
}

/**
 * Wires together {@link useCLIPromptState}, {@link useOptimisticLogs}, and a
 * dedicated {@link useCreateCommand} mutation for components that render an
 * interactive prompt (permission_request, ask_user, exit_plan_mode) and submit
 * the user's response.
 *
 * The hook owns its own createCommand instance so consumers don't have to
 * juggle a shared isPromptResponse flag — the success handler unconditionally
 * marks the prompt resolved, and the error handler unconditionally rewinds
 * its own optimistic record. Consumers can keep a separate createCommand for
 * regular message submission (CLI chat) or steering messages (task chat).
 */
export function usePromptResponseSubmission({
  sessionId,
  enabled,
  onError,
}: UsePromptResponseSubmissionOptions): PromptResponseSubmission {
  const {pendingPrompt, isLoading, markPromptResolved, dismissPrompt} = useCLIPromptState({
    sessionId,
    enabled,
  })
  const {addOptimisticResponse, removeOptimisticResponse} = useOptimisticLogs()
  const optimisticIdRef = useRef<string | null>(null)

  const {createCommand, isPending} = useCreateCommand({
    onSuccess: () => {
      markPromptResolved()
      optimisticIdRef.current = null
    },
    onError: () => {
      if (optimisticIdRef.current) {
        removeOptimisticResponse(optimisticIdRef.current)
        optimisticIdRef.current = null
      }
      onError?.()
    },
  })

  const handlePromptRespond = useCallback(
    (toolCallId: string, response: unknown, commandType: CommandType) => {
      if (!pendingPrompt || isPending || !sessionId) return

      optimisticIdRef.current = addOptimisticResponse({
        toolCallId,
        response,
        commandType,
      })

      // Forward the prompt-specific command type (e.g. PERMISSION_RESPONSE,
      // ASK_USER_RESPONSE) so Mission Control can route the command correctly:
      // CCAv3 steering filters on `command.type !== 'user_message'` to avoid
      // injecting prompt responses as raw user steering messages. Sweagentd is
      // type-blind on its command callback path, so CCAv1 keeps receiving these
      // commands as TypeUserMessage in /agent/jobs responses; the runtime gate
      // matches by promptId in the content body.
      createCommand({
        sessionId,
        content: JSON.stringify(response),
        type: commandType,
      })
    },
    [pendingPrompt, isPending, sessionId, createCommand, addOptimisticResponse],
  )

  return {pendingPrompt, isLoading, isPending, dismissPrompt, handlePromptRespond}
}
